import includes from 'lodash/includes'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import map from 'lodash/map'
import reduce from 'lodash/reduce'

import { DynamicVariable, ExternalLink, Page, Reference, RteField, Scalars } from '@Generated/graphql'

/**
 * Serialization-agnostic resolution of Contentstack JSON-RTE references.
 *
 * Contentstack stores references unresolved; the graph-api returns the resolved items once in a
 * parent `referencesWithKey` (so the same item is only resolved once). This walks the RTE document
 * and merges that reference data — and any `dynamicVariables` overrides — onto each node before it
 * is serialized.
 *
 * Deliberately free of any serializer import so it can be shared without pulling in
 * `@contentstack/json-rte-serializer`.
 */

export const isDynamicVariable = (reference: Reference | undefined): reference is DynamicVariable => reference?.__typename === 'DynamicVariable'
export const isExternalLinkReference = (reference: Reference | undefined): reference is ExternalLink => reference?.__typename === 'ExternalLink'
export const isPageReference = (reference: Reference | undefined): reference is Page => reference?.__typename === 'Page'

const resolveRTEReference = <T extends Scalars['JSON']>(input: T, references: Record<string, Reference>): T => {
  if (!input) {
    return input
  }

  if (isArray(input)) {
    return map(input, item => resolveRTEReference(item, references)) as T
  }

  if (!isObject(input)) {
    return input
  }

  // We have to clone input as it is readonly on server side
  const result = { ...(input as any) }

  if (input.children) {
    result.children = resolveRTEReference(input.children, references)
  }

  if (!input.referenceKey) {
    return result
  }

  const reference = references[input.referenceKey as string]

  if (isDynamicVariable(reference)) {
    result.children = [{ text: reference.value }]

    if (reference.url) {
      result.children = [
        {
          attrs: {
            href: reference.url,
          },
          children: result.children,
          type: 'a',
        },
      ]
    }
  }

  if (isExternalLinkReference(reference) || isPageReference(reference)) {
    // TODO: remove when graph-api doesn't return HTML for ExternalLink anymore
    if (includes(reference.url, '<')) {
      console.warn('url contains html characters', reference.url)
      return result
    }

    if (!result.attrs) {
      result.attrs = {}
    }

    if (isPageReference(reference) && reference.type === 'modals') {
      result.attrs = {
        ...result.attrs,
        'data-modal-id': reference.id,
        href: '#',
      }

      return result
    }

    // We have to clone attrs as they are readonly on server side
    result.attrs = {
      ...result.attrs,
      href: reference.url,
    }
  }

  return result
}

/**
 * Resolves an RTE field's references (merged with `dynamicVariables`) and returns the ready-to-render
 * JSON document — or `null` when there is nothing worth rendering (empty value, or a single empty `<p>`).
 */
export const resolveRTEField = (data: RteField | null | undefined, dynamicVariables: Record<string, string> = {}): any | null => {
  if (!data || !data.value) {
    return null
  }

  let result: any = data.value
  if (data.referencesWithKey && data.referencesWithKey.length !== 0) {
    const references = reduce(data.referencesWithKey, (memo, referenceWithKey) => {
      if (!referenceWithKey || !referenceWithKey.key || !referenceWithKey.reference) {
        return memo
      }

      // eslint-disable-next-line no-param-reassign
      memo[referenceWithKey.key] = referenceWithKey.reference

      if (isDynamicVariable(referenceWithKey.reference) && referenceWithKey.reference?.id && dynamicVariables[referenceWithKey.reference.id]) {
        // eslint-disable-next-line no-param-reassign
        memo[referenceWithKey.key] = {
          ...referenceWithKey.reference,
          value: dynamicVariables[referenceWithKey.reference.id],
        }
      }
      return memo
    }, {})
    result = resolveRTEReference(data.value, references)
  }

  // When the first element is an empty p-tag, there is no reason to render anything
  if (
    result
    && result.type === 'doc'
    && result.children?.length === 1
    && result.children[0]?.type === 'p'
    && result.children[0]?.children?.length === 1
    && result.children[0].children[0]?.text === ''
  ) {
    return null
  }

  return result
}
